import { Injectable, EventEmitter } from '@angular/core';
import { LocalStorageManager } from 'src/app/data/manager/local-storage/local-storage.manager';
import { SessionStorageManager } from '../session-storage/session-storage.manager';
import { ChatProvider, KustomerInterface, kustomerInitEvent, kustomerOptions } from 'src/app/data/networking/models/support/support';
import { ChatService } from 'src/app/ui/shared/services/chat.service';
import { HelpButtonComponent } from 'src/app/ui/shared/modules/help-button/help-button.component';
import { Router } from '@angular/router';

export interface WindowCustomInterface extends Window {
  Kustomer: KustomerInterface;
  loadKustomerChat: () => void;
}

declare const window: WindowCustomInterface;
declare const Kustomer: KustomerInterface;

@Injectable({providedIn: 'root'})
export class SupportManager {

  private initializedChat = false;
  private getUserTokenCount = 0;
  private maxTokenErrorCount = 20;
  private userLogged = false;

  onScriptLoaded = new EventEmitter();

  get user() { return this.localStorageManager.get('customer'); }
    
  constructor(
    private router: Router,
    private chatService: ChatService,
    private sessionStorageManager: SessionStorageManager,
    private localStorageManager: LocalStorageManager,
  ) {
    window.addEventListener(kustomerInitEvent, () => {
      this.loadConfigChat();
      this.onScriptLoaded.emit();
    });
    this.selectChatProvider();
  }

  isKustomerChatActive() { 
    return this.chatProvider === ChatProvider.KUSTOMER; 
  } 

  selectChatProvider(attempts = 0): void {
    const provider: ChatProvider = this.chatProvider;
    const canReload = attempts < 5;
    const isloadKustomerChatLoaded = (typeof window.loadKustomerChat !== 'undefined');
    const providerIsKustomer = provider === ChatProvider.KUSTOMER;
    if (provider === ChatProvider.ZENDESK) {
      this.onScriptLoaded.emit();
    }
    else if(isloadKustomerChatLoaded && providerIsKustomer) {
      window.loadKustomerChat();
    } 
    else if(canReload && providerIsKustomer) {
      const newAttempts = attempts+1;
      setTimeout(() => this.selectChatProvider(newAttempts), 500);
    }
  } 

  loadConfigChat() {    
    window.Kustomer.start(kustomerOptions, () => {
      this.initializedChat = true
      this.getUserToken();
    });
  }

  sessionActive() {
    const customer = this.localStorageManager.get('customer');
    return customer?.isAnonymous === false;
  }

  open() {
    if(!this.isKustomerChatActive()) {
      return HelpButtonComponent.openChat.emit();
    }
    Kustomer?.open();
    this.loginChat();
  }

  helpWithOrder(orderId: string) {
    this.open();
  }
  
  helpWithLogin() {
    window.Kustomer.createConversation({});
  }

  loginChat(jwtToken: string = null) {
    if(this.userLogged || !this.sessionActive()) { return; }
    const errorSource = 'externalToken';
    const errorStatus = '401';
    window.Kustomer.login({ jwtToken }, (callbackResponse, error) => {
      if(error?.source === errorSource && error?.status === errorStatus) {
        if(this.getUserTokenCount<this.maxTokenErrorCount){ this.getUserToken(); }
        else { this.getUserTokenCount = 0; } 
      } 
      else { 
        this.userLogged = true;
        this.getUserTokenCount = this.maxTokenErrorCount;
      } 
    });
  }

  describeCustomer() {
    if(!this.user) { return;}  
    const user = this.user;
    const attributes = {
      emails: [user.email],
      phones: [user.phone],
    };
    window.Kustomer.describeCustomer({attributes});
  }

  get chatProvider() : ChatProvider {
    const support  = this.sessionStorageManager.get('multiCountry')?.support;
    const provider = support?.provider;
    return provider ? provider.toUpperCase() : ChatProvider.UNSELECTED;
  } 

  getUserToken() {
    const user = this.user;
    if(user === null || user.isAnonymous ) { return; }
    const params = { externalId: `${user.id}` , email: user.email};
    this.getUserTokenCount++;
    this.chatService.getUserToken(params).then(({token}) => this.loginChat(token));
  }

  openChat(idOrder) {
    if(this.isKustomerChatActive()) { 
      if(idOrder) this.helpWithOrder(idOrder); else this.open();
    } else {
      if(idOrder) {
        this.router.navigate(['/cuenta/soporte/orden', idOrder]);
      } else {
        HelpButtonComponent.openChat.emit();
      } 
    } 
  }
  
}